import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  scheduledScansApi,
  type ScanDefinition,
  type ScheduleCadence,
  type ScheduledScanSchedule,
  type ScheduleWriteInput,
} from '../../services/scheduledScansApi';
import { nextSchedulePreviewText } from '../../domain/scheduledScans/scheduleRecurrencePreview';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const WINDOW_OPTIONS = [
  { value: 3600, label: 'Previous hour' },
  { value: 86400, label: 'Previous day' },
  { value: 604800, label: 'Previous 7 days' },
];

export type RepeatScanSetup = {
  version: 1;
  sourceWorkflowId: string;
  siteId: string;
  name: string;
  cameraId: string;
  cameraLabel: string;
  zoneId: string;
  zoneLabel: string;
  riskType: string;
  safetyCaseId: string;
  windowSeconds: number;
};

type FormState = {
  enabled: boolean;
  cadence: ScheduleCadence;
  dayOfWeek: number;
  executionTime: string;
  timezone: string;
  windowSeconds: number;
  settlementSeconds: number;
  retentionPolicyId: string;
  recipientEmails: string;
};

function defaultForm(timezone = 'UTC'): FormState {
  return {
    enabled: false,
    cadence: 'DAILY',
    dayOfWeek: 0,
    executionTime: '09:00',
    timezone,
    windowSeconds: 86400,
    settlementSeconds: 300,
    retentionPolicyId: 'default',
    recipientEmails: '',
  };
}

function repeatForm(setup: RepeatScanSetup | null | undefined, timezone = 'UTC'): FormState {
  return {
    ...defaultForm(timezone),
    enabled: Boolean(setup),
    windowSeconds: setup?.windowSeconds || 86400,
  };
}

function workflowContext(definition: ScanDefinition): Record<string, unknown> {
  const context = definition.engine_config?.workflow_context;
  return context && typeof context === 'object' ? context as Record<string, unknown> : {};
}

function definitionForWorkflow(
  definitions: ScanDefinition[],
  setup: RepeatScanSetup,
): ScanDefinition | undefined {
  return definitions.find(
    (definition) => String(workflowContext(definition).source_workflow_id || '').trim()
      === setup.sourceWorkflowId,
  );
}

function repeatScope(setup: RepeatScanSetup) {
  return {
    camera_ids: setup.cameraId ? [setup.cameraId] : [],
    zone_ids: setup.zoneId ? [setup.zoneId] : [],
    ...(setup.riskType ? { policy_type: setup.riskType, incident_type: setup.riskType } : {}),
  };
}

function repeatDefinitionInput(setup: RepeatScanSetup) {
  return {
    name: setup.name,
    description: 'Recurring Safety Scan created from Risk Pattern Review.',
    scope: repeatScope(setup),
    engine_config: {
      workflow_context: {
        version: setup.version,
        source_workflow_id: setup.sourceWorkflowId,
        safety_case_id: setup.safetyCaseId || null,
      },
    },
  };
}

function sameRepeatDefinition(definition: ScanDefinition, setup: RepeatScanSetup): boolean {
  const scope = definition.scope || {};
  const expected = repeatScope(setup);
  const strings = (value: unknown) => Array.isArray(value) ? value.map(String).sort() : [];
  const context = workflowContext(definition);
  return (
    strings(scope.camera_ids).join('|') === expected.camera_ids.join('|')
    && strings(scope.zone_ids).join('|') === expected.zone_ids.join('|')
    && String(scope.policy_type || '') === setup.riskType
    && String(scope.incident_type || '') === setup.riskType
    && Number(context.version) === setup.version
    && String(context.source_workflow_id || '') === setup.sourceWorkflowId
    && String(context.safety_case_id || '') === setup.safetyCaseId
    && definition.name === setup.name
  );
}

function formatWindow(seconds: number): string {
  if (seconds % 86400 === 0) {
    const days = seconds / 86400;
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  if (seconds % 3600 === 0) {
    const hours = seconds / 3600;
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  return `${seconds} seconds`;
}

function repeatScopeLabel(setup: RepeatScanSetup): string {
  return [
    setup.zoneLabel || setup.zoneId,
    setup.cameraLabel || setup.cameraId,
  ].filter(Boolean).join(' · ');
}

function formFromSchedule(schedule: ScheduledScanSchedule | null, defaultTimezone = 'UTC'): FormState {
  if (!schedule) return defaultForm(defaultTimezone);
  return {
    enabled: schedule.enabled,
    cadence: schedule.cadence === 'OFF' ? 'DAILY' : schedule.cadence,
    dayOfWeek: schedule.day_of_week ?? 0,
    executionTime: String(schedule.execution_time_local || '09:00').slice(0, 5),
    timezone: schedule.timezone || 'UTC',
    windowSeconds: Number(schedule.window_length_seconds || 86400),
    settlementSeconds: Number(schedule.settlement_delay_seconds || 0),
    retentionPolicyId: schedule.retention_policy_id || 'default',
    recipientEmails: (schedule.recipients || [])
      .filter((recipient) => recipient.recipient_type === 'EMAIL' && recipient.enabled !== false)
      .map((recipient) => recipient.email_address)
      .filter(Boolean)
      .join(', '),
  };
}

function writeInput(form: FormState): ScheduleWriteInput {
  const emails = form.recipientEmails
    .split(',')
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  return {
    enabled: form.enabled,
    cadence: form.cadence,
    day_of_week: form.cadence === 'WEEKLY' ? form.dayOfWeek : null,
    execution_time_local: form.executionTime,
    timezone: form.timezone.trim(),
    window_length_seconds: form.windowSeconds,
    settlement_delay_seconds: form.settlementSeconds,
    retention_policy_id: form.retentionPolicyId.trim(),
    recipients: emails.map((email_address) => ({
      recipient_type: 'EMAIL',
      email_address,
      enabled: true,
    })),
  };
}

export default function ScheduledScanConfigurationCard({
  siteId,
  canConfigure,
  defaultTimezone = 'UTC',
  repeatSetup,
  onSelectionChange,
}: {
  siteId: string;
  canConfigure: boolean;
  defaultTimezone?: string;
  repeatSetup?: RepeatScanSetup | null;
  onSelectionChange?: (selection: {
    definitionId: string;
    definitionName: string;
    scheduleId: string | null;
  }) => void;
}) {
  const [available, setAvailable] = useState<boolean | null>(null);
  const [definitions, setDefinitions] = useState<ScanDefinition[]>([]);
  const [definitionId, setDefinitionId] = useState('');
  const [schedule, setSchedule] = useState<ScheduledScanSchedule | null>(null);
  const [form, setForm] = useState<FormState>(repeatForm(repeatSetup, defaultTimezone));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([scheduledScansApi.featureEnabled(), scheduledScansApi.listDefinitions(siteId)])
      .then(([enabled, items]) => {
        if (cancelled) return;
        setAvailable(enabled);
        const activeItems = items.filter((item) => item.status === 'ACTIVE');
        setDefinitions(activeItems);
        const repeated = repeatSetup ? definitionForWorkflow(activeItems, repeatSetup) : undefined;
        setDefinitionId((current) => (
          repeated?.scan_definition_id || current || activeItems[0]?.scan_definition_id || ''
        ));
        setForm((current) => repeatSetup
          ? { ...current, enabled: true, windowSeconds: repeatSetup.windowSeconds }
          : current);
      })
      .catch((cause) => {
        if (!cancelled) {
          setAvailable(false);
          setError(cause?.response?.data?.detail || cause?.message || 'Scheduled Scans unavailable');
          setForm(defaultForm(defaultTimezone));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canConfigure, defaultTimezone, repeatSetup, siteId]);

  const loadSchedule = useCallback(async () => {
    if (!definitionId || available !== true) {
      setSchedule(null);
      setForm(repeatForm(repeatSetup, defaultTimezone));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const schedules = await scheduledScansApi.listSchedules(siteId, definitionId);
      const selected = schedules.find((item) => item.enabled) || schedules[0] || null;
      setSchedule(selected);
      setForm(formFromSchedule(selected, defaultTimezone));
    } catch (cause: any) {
      setError(cause?.response?.data?.detail || cause?.message || 'Unable to load schedule');
    } finally {
      setLoading(false);
    }
  }, [available, defaultTimezone, definitionId, repeatSetup, siteId]);

  useEffect(() => {
    void loadSchedule();
  }, [loadSchedule]);

  useEffect(() => {
    const definition = definitions.find((item) => item.scan_definition_id === definitionId);
    onSelectionChange?.({
      definitionId,
      definitionName: definition?.name || '',
      scheduleId: schedule?.id || null,
    });
  }, [definitionId, definitions, onSelectionChange, schedule?.id]);

  const preview = useMemo(
    () =>
      nextSchedulePreviewText({
        enabled: form.enabled,
        cadence: form.cadence,
        dayOfWeek: form.dayOfWeek,
        executionTime: form.executionTime,
        timezone: form.timezone,
        windowSeconds: form.windowSeconds,
      }),
    [form],
  );
  const windowOptions = useMemo(() => (
    WINDOW_OPTIONS.some((option) => option.value === form.windowSeconds)
      ? WINDOW_OPTIONS
      : [{ value: form.windowSeconds, label: `Previous ${formatWindow(form.windowSeconds)}` }, ...WINDOW_OPTIONS]
  ), [form.windowSeconds]);
  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const input = writeInput(form);
      const next = schedule
        ? await scheduledScansApi.updateSchedule(siteId, schedule.id, {
            ...input,
            expected_version: schedule.version,
          })
        : await scheduledScansApi.createSchedule(siteId, definitionId, input);
      setSchedule(next);
      setForm(formFromSchedule(next, defaultTimezone));
      setSaved(true);
    } catch (cause: any) {
      const detail = cause?.response?.data?.detail || cause?.message || 'Unable to save schedule';
      const conflict = cause?.response?.data?.error === 'version_conflict';
      setError(conflict ? 'This schedule changed elsewhere. Reload before saving again.' : detail);
    } finally {
      setSaving(false);
    }
  };

  const saveRepeat = async () => {
    if (!repeatSetup || !canConfigure) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const existing = definitionForWorkflow(definitions, repeatSetup);
      const input = repeatDefinitionInput(repeatSetup);
      let nextDefinition = existing;
      if (!existing) {
        nextDefinition = await scheduledScansApi.createDefinition(siteId, input);
      } else if (!sameRepeatDefinition(existing, repeatSetup)) {
        nextDefinition = await scheduledScansApi.reviseDefinition(
          siteId,
          existing.scan_definition_id,
          { ...input, expected_version: existing.version },
        );
      }
      if (!nextDefinition) throw new Error('Unable to save the Safety Scan definition');
      const scheduleInput = writeInput(form);
      const existingSchedules = schedule && schedule.scan_definition_id === nextDefinition.scan_definition_id
        ? [schedule]
        : await scheduledScansApi.listSchedules(siteId, nextDefinition.scan_definition_id);
      const existingSchedule = existingSchedules.find((item) => item.enabled)
        || existingSchedules[0]
        || null;
      const nextSchedule = existingSchedule
        ? await scheduledScansApi.updateSchedule(siteId, existingSchedule.id, {
            ...scheduleInput,
            expected_version: existingSchedule.version,
          })
        : await scheduledScansApi.createSchedule(
            siteId,
            nextDefinition.scan_definition_id,
            scheduleInput,
          );
      setDefinitions((current) => [
        nextDefinition!,
        ...current.filter((item) => item.scan_definition_id !== nextDefinition!.scan_definition_id),
      ]);
      setDefinitionId(nextDefinition.scan_definition_id);
      setSchedule(nextSchedule);
      setForm(formFromSchedule(nextSchedule, defaultTimezone));
      setSaved(true);
    } catch (cause: any) {
      const detail = cause?.response?.data?.detail || cause?.message || 'Unable to schedule this Safety Scan';
      const conflict = cause?.response?.data?.error === 'version_conflict';
      setError(conflict ? 'This Safety Scan changed elsewhere. Reload before saving again.' : detail);
    } finally {
      setSaving(false);
    }
  };

  if (loading && available === null) {
    return <CircularProgress size={24} aria-label="Loading scheduled scans" />;
  }
  if (available === false) return null;

  return (
    <Card variant="outlined" data-testid="scheduled-scan-configuration" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 750 }}>
              {repeatSetup ? 'Repeat this Safety Scan' : 'Manage recurring scans'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {repeatSetup
                ? `Repeat ${repeatSetup.name} · ${repeatScopeLabel(repeatSetup)} · previous ${formatWindow(repeatSetup.windowSeconds)}.`
                : 'Manage cadence, recipients, and enabled state for existing recurring Safety Scans.'}
            </Typography>
          </Box>
          {repeatSetup && !canConfigure ? (
            <Alert severity="info">scan:configure access is required to schedule this Safety Scan.</Alert>
          ) : null}
          {error ? <Alert severity="warning">{error}</Alert> : null}
          {saved ? <Alert severity="success">Schedule saved.</Alert> : null}
          {definitions.length === 0 && !repeatSetup ? (
            <Alert severity="info">
              Recurring Safety Scans are configured from a Risk Pattern Review. No existing scans are available to manage for this site.
            </Alert>
          ) : null}
          {definitions.length > 0 || repeatSetup ? (
            <>
              <Divider />
              {!repeatSetup ? (
                <TextField
                  select
                  label="Scan definition"
                  value={definitionId}
                  onChange={(event) => setDefinitionId(event.target.value)}
                  disabled={loading || saving}
                >
                  {definitions.map((definition) => (
                    <MenuItem key={definition.scan_definition_id} value={definition.scan_definition_id}>
                      {definition.name}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
                <TextField
                  select
                  label="Cadence"
                  value={form.cadence}
                  onChange={(event) => update('cadence', event.target.value as ScheduleCadence)}
                  disabled={!canConfigure}
                >
                  <MenuItem value="DAILY">Daily</MenuItem>
                  <MenuItem value="WEEKLY">Weekly</MenuItem>
                </TextField>
                {form.cadence === 'WEEKLY' ? (
                  <TextField
                    select
                    label="Day"
                    value={form.dayOfWeek}
                    onChange={(event) => update('dayOfWeek', Number(event.target.value))}
                    disabled={!canConfigure}
                  >
                    {WEEKDAYS.map((label, index) => <MenuItem key={label} value={index}>{label}</MenuItem>)}
                  </TextField>
                ) : null}
                <TextField
                  label="Local execution time"
                  type="time"
                  value={form.executionTime}
                  onChange={(event) => update('executionTime', event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={!canConfigure}
                />
                <TextField
                  label="IANA timezone"
                  value={form.timezone}
                  onChange={(event) => update('timezone', event.target.value)}
                  placeholder="America/Toronto"
                  disabled={!canConfigure}
                />
                <TextField
                  select
                  label="Observation window"
                  value={form.windowSeconds}
                  onChange={(event) => update('windowSeconds', Number(event.target.value))}
                  disabled={!canConfigure}
                >
                  {windowOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Settlement delay (seconds)"
                  type="number"
                  value={form.settlementSeconds}
                  onChange={(event) => update('settlementSeconds', Math.max(0, Number(event.target.value)))}
                  disabled={!canConfigure}
                />
                <TextField
                  label="Retention policy"
                  value={form.retentionPolicyId}
                  onChange={(event) => update('retentionPolicyId', event.target.value)}
                  disabled={!canConfigure}
                />
              </Box>
              <TextField
                label="Digest recipients"
                value={form.recipientEmails}
                onChange={(event) => update('recipientEmails', event.target.value)}
                helperText="Comma-separated authorized email addresses"
                disabled={!canConfigure}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.enabled}
                      onChange={(event) => update('enabled', event.target.checked)}
                      disabled={!canConfigure}
                    />
                  }
                  label="Enabled"
                />
                <Typography variant="body2" color="text.secondary">
                  {`Next occurrence preview: ${preview}`}
                </Typography>
                {canConfigure ? (
                  <Button
                    variant="contained"
                    onClick={() => void (repeatSetup ? saveRepeat() : save())}
                    disabled={saving || loading}
                  >
                    {saving
                      ? 'Saving…'
                      : repeatSetup
                        ? 'Schedule this Safety Scan'
                        : schedule ? 'Save schedule' : 'Create schedule'}
                  </Button>
                ) : null}
              </Stack>
            </>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}
